import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Catches a render error and shows it.
 *
 * React unmounts the whole tree when a render throws, which leaves a blank
 * white page and the actual message only in the browser console. That is a
 * miserable failure mode — it looks like the app did not load rather than that
 * one component broke, and it has already cost us twice.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Fieldpath render error", error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="wrap" style={{ paddingTop: 40 }}>
        <div className="card">
          <h1>Something broke on this page</h1>
          <p className="muted">
            The rest of Canopy is fine — reload, or go back and try again. If it
            keeps happening, the detail below is what to report.
          </p>
          <pre
            style={{
              background: "var(--bg)",
              border: "1px solid var(--line)",
              borderRadius: 6,
              padding: 12,
              overflowX: "auto",
              fontSize: 13,
              whiteSpace: "pre-wrap",
            }}
          >
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ""}
          </pre>
          <button className="primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </main>
    );
  }
}
